import { computed } from "vue";
import { SportIcon, TournamentIcon } from "@xs2event/js-lib/icons";
import { sportTypes } from "@xs2event/js-lib/util";
import { useMainMenuEntries } from "../useMainMenuEntries";

// From this many entries with their own links, a row of columns gets too wide: show them as a drilldown list.
const DRILLDOWN_MIN_ENTRIES = 5;

export type FeaturedGroup = { label: string; links: MenuNode[] };
export type MenuGroup = { label: string; links: MenuNode[]; columns?: number };

const MAX_GROUP_ROWS = 10;

// The menu columns a group spans: one per `MAX_GROUP_ROWS` links, or its own `columns` (no more than it has links).
export const groupColumns = (group: { links: unknown[]; columns?: number }) =>
  group.columns
    ? Math.min(group.columns, group.links.length)
    : Math.max(1, Math.ceil(group.links.length / MAX_GROUP_ROWS));
export type MenuNode = {
  label: string;
  to?: string;
  flag?: string;
  button?: boolean;
  items?: MenuNode[];
  featured?: FeaturedGroup;
  params?: { sport_type?: string };
};

// The icon of a drilldown entry without a flag: its sport's icon, or a tournament icon.
export const entryIcon = (entry: MenuNode) => {
  const sportType = entry.params?.sport_type;
  if (!sportType) return TournamentIcon;
  return sportTypes[sportType]?.icon ?? SportIcon;
};

// Loose links become one group under `leafLabel` (over `leafColumns` columns, when given); nodes with links become a
// group each (or several, when nested).
export const toGroups = (nodes: MenuNode[], leafLabel: string, leafColumns?: number): MenuGroup[] => {
  const isLink = (node: MenuNode) => !node.button && !node.items?.length && node.to;
  const links = nodes.filter(isLink);
  const groups = nodes
    .filter((node) => !node.button && node.items?.length)
    .flatMap((node) =>
      node.items!.some((child) => child.items?.length)
        ? toGroups(node.items!, node.label)
        : [{ label: node.label, links: node.items!.filter(isLink) }]
    );

  return [...(links.length ? [{ label: leafLabel, links, columns: leafColumns }] : []), ...groups].filter(
    (group) => group.links.length
  );
};

// The sections of one main menu item's submenu, shared by the desktop mega menu and the mobile menu: a drilldown list
// of entries (countries, sports) whose groups open on hover/tap, or plain groups, plus the featured groups.
export function useMegaMenu(item) {
  const { entries } = useMainMenuEntries(item);

  // "View all" buttons are left out; the nav item itself links to the events page.
  const visibleEntries = computed<MenuNode[]>(() => (entries.value ?? []).filter((entry) => !entry.button));
  // Entries with a `featured` group (football's international leagues) move to the featured groups.
  const drilldownEntries = computed(() =>
    visibleEntries.value.filter((entry) => entry.items?.length && !entry.featured)
  );

  const featuredGroups = computed<FeaturedGroup[]>(() =>
    [...visibleEntries.value.map((entry) => entry.featured), item.featured].filter(
      (group): group is FeaturedGroup => !!group?.links.length
    )
  );

  const isDrilldown = computed(
    () =>
      drilldownEntries.value.length >= DRILLDOWN_MIN_ENTRIES ||
      drilldownEntries.value.some((entry) => entry.items!.some((child) => child.items?.length))
  );

  const entriesLabel = computed(() => (drilldownEntries.value.some((entry) => entry.flag) ? "Countries" : "Sports"));

  // A single submenu is unwrapped to its links by useMainMenuEntries; it keeps its own label as the group heading.
  const groupsLabel = computed(() => (item.items?.length === 1 ? item.items[0].label : item.label));

  return { visibleEntries, drilldownEntries, featuredGroups, isDrilldown, entriesLabel, groupsLabel };
}
